import React, { useState, useCallback, useEffect, useRef } from "react";
import AsyncSelect from "react-select/async";
import DischargeReceiptModal from "./DischargeReceiptModal";
import Select from "react-select";
import { customFetch } from '../apiClient';
import DiscountAuthorityDropdown from '../components/DiscountAuthorityDropdown';

const DischargeBill = () => {
  // ==================== STATES ====================
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const token = sessionStorage.getItem("token");
  const [receiptData, setReceiptData] = useState(null);
  const [loggedInUser, setLoggedInUser] = useState(null);
  const [companyDetails, setCompanyDetails] = useState(null);
  const [showReceipt, setShowReceipt] = useState(false);
  const [loading, setLoading] = useState(false);

  // Advance States
  const [advancePayments, setAdvancePayments] = useState([]);
  const [loadingAdv, setLoadingAdv] = useState(false);
  const [advEditId, setAdvEditId] = useState(null);
  const [advEditData, setAdvEditData] = useState({});

  // Service States
  const [services, setServices] = useState([]);
  const [loadingServices, setLoadingServices] = useState(false);
  const [serviceEditId, setServiceEditId] = useState(null);
  const [serviceEditData, setServiceEditData] = useState({});
  const [selectedServiceIds, setSelectedServiceIds] = useState([]);
  const [selectAllServices, setSelectAllServices] = useState(false);

  // Room States
  const [rooms, setRooms] = useState([]);
  const [loadingRooms, setLoadingRooms] = useState(false);
  const [roomExitMap, setRoomExitMap] = useState({});

  // ✅ NEW: Financial Summary State
  const [finSummary, setFinSummary] = useState(null);

  // Form State
  const [dischargeForm, setDischargeForm] = useState({
    billDate: new Date().toISOString().substring(0, 10),
    dischargeDate: new Date().toISOString().substring(0, 10),
    remarks: "",
    totalAmount: 0,
    discountType: "amount",
    discountValue: 0,
    discount: 0,
    advance: 0,
    payable: 0,
    received: 0,
    due: 0,
    disAuthority: "",
    docPreparedby: "",
    patientStatus: false,
    nofDays: 1, // Default 1
  });

  const [showDischargeForm, setShowDischargeForm] = useState(true);
  const [dischargingPatient, setDischargingPatient] = useState(false);
  const formRef = useRef(null);

  const discountTypeOptions = [
    { value: "amount", label: "Amount (₹)" },
    { value: "percent", label: "Percent (%)" }
  ];

 // ==================== KEYBOARD NAVIGATION ====================
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key !== 'Enter') return;
      if (e.target.closest('.react-select__menu')) return;
      if (e.target.closest('.react-select__input-container')) return;

      const focusableSelector =
        'input:not([disabled]):not([type="hidden"]):not([readonly]), ' +
        'select:not([disabled]), ' +
        'textarea:not([disabled]):not([readonly]), ' +
        'button:not([disabled])';

      const focusableElements = Array.from(
        formRef.current?.querySelectorAll(focusableSelector) || []
      );

      if (focusableElements.length === 0) return;

      const currentIndex = focusableElements.indexOf(document.activeElement);

      if (currentIndex === focusableElements.length - 1) {
        e.preventDefault();
        formRef.current?.requestSubmit();
        return;
      }

      if (currentIndex >= 0) {
        e.preventDefault();
        focusableElements[currentIndex + 1]?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

 // ==================== API LOADERS ====================
  const loadIpdOptions = useCallback(async (inputValue) => {
    if (!inputValue) return [];
    try {
     const res = await customFetch(`/Ipds/list?search=${encodeURIComponent(inputValue)}`);
      if (!res.ok) return [];
      const data = await res.json();
      
      return data.map((item) => {
        const regDateFormatted = item.regDate || item.ipD_Date
            ? new Date(item.regDate || item.ipD_Date).toLocaleDateString('en-IN', { 
                day: '2-digit', month: 'short', year: 'numeric' 
              }) 
            : "N/A";

        return {
          label: `${item.name} (${item.mobileNo}) | UHID: ${item.uhiD_No} | IPD No: ${item.ipD_SRNO} | Reg Date: ${regDateFormatted}`,
          value: item.ipD_ID,
          raw: item
        };
      });
    } catch { 
      return []; 
    }
  }, [token]);

  useEffect(() => {
    const hospitalId = sessionStorage.getItem("hospitalId");
   if (hospitalId) {
  customFetch(`/Company/${hospitalId}`)
    .then(res => res.json())
        .then(data => setCompanyDetails(data))
        .catch(console.error);
    }
    const user = sessionStorage.getItem("loggedInUser");
    if (user) setLoggedInUser(user);
  }, [token]);

  const fetchAdvancePayments = useCallback(async (ipdId) => {
    setLoadingAdv(true);
    try {
     const res = await customFetch(`/IpdAdvDt/search-by-ipd?ipdId=${ipdId}`);
      if (!res.ok) throw new Error("Failed");
      const data = await res.json();
      setAdvancePayments(data);
    } catch (e) {
      setAdvancePayments([]);
    } finally { setLoadingAdv(false); }
  }, [token]);

  const fetchServices = useCallback(async (ipdId) => {
    setLoadingServices(true);
    try {
      const res = await customFetch(`/IPDServiceDetailDT/by-ipd/${ipdId}`);
      if (!res.ok) throw new Error("Failed");
      const data = await res.json();
      setServices(data);
    } catch (e) { setServices([]); } finally { setLoadingServices(false); }
  }, [token]);

  const fetchRooms = useCallback(async (ipdId) => {
    setLoadingRooms(true);
    try {
    const res = await customFetch(`/IPDRoom/search-by-ipd?rIpdId=${ipdId}`);
      if (!res.ok) throw new Error("Failed");
      const data = await res.json();
      setRooms(data);
      const map = {};
      data.forEach((r) => { if (r.exitDate) map[r.rM_TID] = r.exitDate.substring(0, 16); });
      setRoomExitMap(map);
    } catch (e) { setRooms([]); setRoomExitMap({}); } finally { setLoadingRooms(false); }
  }, [token]);

  // ✅ NEW: Fetch Financial Summary
  const fetchFinancialSummary = useCallback(async (ipdId) => {
    try {
      const res = await customFetch(`/DischargeDt/ipd-financial-summary/${ipdId}`);
      if (res.ok) {
        const data = await res.json();
        setFinSummary(data);
      }
    } catch (e) { console.error("Fin summary fetch failed", e); }
  }, [token]);

  useEffect(() => {
    if (!selectedPatient) {
      setAdvancePayments([]); setServices([]); setRooms([]); setFinSummary(null);
      setShowDischargeForm(false); return;
    }
    setError(null); setMessage(null); setShowDischargeForm(true);
    fetchAdvancePayments(selectedPatient.value);
    fetchServices(selectedPatient.value);
    fetchRooms(selectedPatient.value);
    fetchFinancialSummary(selectedPatient.value); // ✅ Call API
  }, [selectedPatient, fetchAdvancePayments, fetchServices, fetchRooms, fetchFinancialSummary]);

  // ==================== CALCULATIONS ====================
  useEffect(() => {
    if (!selectedPatient) return;

    const totalFromServices = services.reduce((sum, s) => sum + (Number(s.amount) || 0), 0);

    const totalFromRooms = rooms.reduce((sum, room) => {
      if (!room.entryDate) return sum;
      const entry = new Date(room.entryDate);
      let exit;
      if (room.exitDate) {
        exit = new Date(room.exitDate);
      } else {
        exit = new Date(dischargeForm.dischargeDate);
        const now = new Date();
        exit.setHours(now.getHours(), now.getMinutes(), 0, 0);
      }
      const diffTime = exit.getTime() - entry.getTime();
      const diffHours = diffTime / (1000 * 60 * 60);
      let days = Math.ceil(diffHours / 24);
      days = Math.max(1, days); 
      const roomTotal = days * (Number(room.roomRate) || 0);
      return sum + roomTotal;
    }, 0);

    const totalAdvance = advancePayments.reduce((sum, a) => sum + (Number(a.adv_Amt) || 0), 0);

    // ✅ THE NEW CALCULATION LOGIC
    let grandTotal = 0;
    let finalPayable = 0;

    if (finSummary && finSummary.hasPackage) {
        // Scenario 1: Package Included
        grandTotal = Number(finSummary.packageAmount) || 0;
        
        let deductions = totalAdvance;
        if (finSummary.isBillingIncluded) deductions += Number(finSummary.billingPaid || 0);
        if (finSummary.isMedicineIncluded) deductions += Number(finSummary.medicinePaid || 0);

        let tempDiscount = dischargeForm.discountType === "percent"
          ? (grandTotal * Number(dischargeForm.discountValue)) / 100
          : Number(dischargeForm.discountValue);

        finalPayable = grandTotal - tempDiscount - deductions;
    } else {
        // Scenario 2: No Package (Old Logic)
        grandTotal = totalFromServices + totalFromRooms;
        let tempDiscount = dischargeForm.discountType === "percent"
          ? (grandTotal * Number(dischargeForm.discountValue)) / 100
          : Number(dischargeForm.discountValue);
          
        finalPayable = grandTotal - tempDiscount - totalAdvance;
    }

    let calculatedDiscount = dischargeForm.discountType === "percent"
      ? (grandTotal * Number(dischargeForm.discountValue)) / 100
      : Number(dischargeForm.discountValue);

    const dueAmount = finalPayable - Number(dischargeForm.received);

    let calculatedDays = 1;
    if (selectedPatient.raw) {
        const rawDate = selectedPatient.raw.regDate || selectedPatient.raw.ipD_Date;
        if (rawDate) {
            const admitDate = new Date(rawDate);
            const dischargeDateObj = new Date(dischargeForm.dischargeDate);
            const now = new Date();
            dischargeDateObj.setHours(now.getHours(), now.getMinutes(), 0, 0);
            const diffTime = dischargeDateObj.getTime() - admitDate.getTime();
            const diffHours = diffTime / (1000 * 60 * 60);
            const diffDays = Math.ceil(diffHours / 24);
            calculatedDays = Math.max(1, diffDays);
        }
    }

    setDischargeForm((prev) => ({
      ...prev,
      totalAmount: grandTotal, // Displaying Grand Total properly based on package or regular
      advance: totalAdvance,
      serviceTotal: totalFromServices,
      roomTotal: totalFromRooms,
      discount: calculatedDiscount,
      payable: finalPayable > 0 ? finalPayable : 0,
      due: dueAmount,
      nofDays: calculatedDays,
    }));
  }, [services, rooms, advancePayments, dischargeForm.discountType, dischargeForm.discountValue, dischargeForm.received, dischargeForm.dischargeDate, selectedPatient, finSummary]);

  const consolidatedServices = React.useMemo(() => {
    const map = {};
    services.forEach(s => {
      const key = s.serviceName || "Unknown Service";
      if (!map[key]) {
        map[key] = {
          serviceName: key,
          qty: 0,
          servRate: s.servRate || 0,
          amount: 0
        };
      }
      map[key].qty += Number(s.qty || 1);
      map[key].amount += Number(s.amount || 0);
    });
    return Object.values(map);
  }, [services]);

  // ==================== HANDLERS ====================
  // [...keeping all existing handlers intact: startAdvEditing, handleAdvChange, saveAdvance, deleteAdvance, startServiceEditing, etc.]
  const startAdvEditing = (record) => {
    setAdvEditId(record.advID);
    setAdvEditData({
      adv_Amt: record.adv_Amt ? record.adv_Amt.toFixed(2) : "",
      recvDate: record.recvDate ? record.recvDate.substring(0, 10) : "",
      remarks: record.remarks || "",
      payMode: record.payMode || "Cash",
      chqNo: record.chqNo || "",
      chqDate: record.chqDate ? record.chqDate.substring(0, 10) : "",
    });
  };
  const handleAdvChange = (f, v) => {
    setAdvEditData(prev => {
      const up = { ...prev, [f]: v };
      if (f === "payMode" && (v === "Cash" || v === "UPI")) { up.chqNo = ""; up.chqDate = ""; }
      return up;
    });
  };
  const saveAdvance = async () => {
    if (!advEditId) return;
    const amtNum = parseFloat(advEditData.adv_Amt);
    if (isNaN(amtNum) || amtNum <= 0) { setError("Invalid amount"); return; }
    setLoadingAdv(true);
    try {
      const originalRecord = advancePayments.find((r) => r.advID === advEditId);
      const updatedRecord = { ...originalRecord, ...advEditData, adv_Amt: amtNum };
      const res = await customFetch(`/IpdAdvDt/${advEditId}`, {
  method: "PUT",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify(updatedRecord),
});
      if (!res.ok) throw new Error("Failed update");
      await fetchAdvancePayments(selectedPatient.value);
      setAdvEditId(null);
      setMessage("Advance updated.");
    } catch (e) { setError(e.message); } finally { setLoadingAdv(false); }
  };
  const deleteAdvance = async (id) => {
    if (!window.confirm("Delete this advance?")) return;
    setLoadingAdv(true);
    try {
     const res = await customFetch(`/IpdAdvDt/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed delete");
      await fetchAdvancePayments(selectedPatient.value);
      setMessage("Deleted successfully.");
    } catch (e) { setError(e.message); } finally { setLoadingAdv(false); }
  };
const startServiceEditing = (s) => { 
    setServiceEditId(s.servSrNo); 
    const servdateValue = s.servdate ? new Date(s.servdate).toISOString().slice(0, 16) : "";
    setServiceEditData({ 
      qty: s.qty, 
      servRate: s.servRate, 
      amount: s.amount, 
      remarks: s.remarks,
      servdate: servdateValue 
    }); 
  };

  const handleServiceChange = (f, v) => setServiceEditData(p => {
    const up = { ...p, [f]: v };
    if (f === "qty" || f === "servRate") up.amount = (up.qty * up.servRate).toFixed(2);
    return up;
  });

  const saveService = async () => {
    if (!serviceEditId) return;
    setLoadingServices(true);
    try {
      const sIndex = services.findIndex(s => s.servSrNo === serviceEditId);
      const sData = services[sIndex];
   const res = await customFetch(`/IPDServiceDetailDT/${serviceEditId}`, {
  method: "PUT",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify({
          ...sData, 
          ...serviceEditData, 
          qty: parseFloat(serviceEditData.qty), 
          servRate: parseFloat(serviceEditData.servRate), 
          amount: Number(serviceEditData.amount),
          servdate: serviceEditData.servdate 
        })
      });
      if (!res.ok) throw new Error("Failed update");
      setServices(prev => {
        const newS = [...prev];
        newS[sIndex] = { ...newS[sIndex], ...serviceEditData, amount: Number(serviceEditData.amount) };
        return newS;
      });
      setServiceEditId(null);
      setMessage("Service updated.");
    } catch (e) { setError(e.message); } finally { setLoadingServices(false); }
  };
  const toggleServiceSelect = (id) => setSelectedServiceIds(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  const toggleSelectAllServices = () => {
    if (selectAllServices) { setSelectedServiceIds([]); setSelectAllServices(false); }
    else { setSelectedServiceIds(services.map(s => s.servSrNo)); setSelectAllServices(true); }
  };
  const deleteService = async (id = null) => {
    const ids = id ? [id] : selectedServiceIds;
    if (ids.length === 0) return setError("Select items");
    if (!window.confirm("Delete selected services?")) return;
    setLoadingServices(true);
    try {
      for (const delId of ids) {
  await customFetch(`/IPDServiceDetailDT/${delId}`, { method: "DELETE" });
}
      setServices(p => p.filter(s => !ids.includes(s.servSrNo)));
      setSelectedServiceIds([]); setSelectAllServices(false); setMessage("Deleted.");
    } catch (e) { setError(e.message); } finally { setLoadingServices(false); }
  };

  const handleRoomExitChange = (id, v) => setRoomExitMap(p => ({ ...p, [id]: v }));
  const updateExitDate = async (room) => {
    if (!roomExitMap[room.rM_TID]) return setError("Pick Exit Date");
    setLoadingRooms(true);
    try {
     const res = await customFetch(`/IPDRoom/deallocate/${room.rM_TID}`, {
  method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ exitDate: roomExitMap[room.rM_TID] })
});
      if (!res.ok) throw new Error("Failed");
      await fetchRooms(selectedPatient.value); setMessage("Exit Updated.");
    } catch (e) { setError(e.message); } finally { setLoadingRooms(false); }
  };
  const handleRoomDelete = async (id) => {
    if (!window.confirm("Delete Room?")) return;
    setLoadingRooms(true);
    try {
      const res = await customFetch(`/IPDRoom/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed");
      await fetchRooms(selectedPatient.value); setMessage("Room Deleted.");
    } catch (e) { setError(e.message); } finally { setLoadingRooms(false); }
  };

  const handleDischargeFormChange = (f, v) => setDischargeForm(p => ({ ...p, [f]: v }));
  const handleDiscountTypeChange = (selectedOption) => {
    setDischargeForm(prev => ({ ...prev, discountType: selectedOption.value, discountValue: 0 }));
  };

  const handleDischargeSubmit = async () => {
    if (!selectedPatient) return setError("Select patient first");
    if (!dischargeForm.docPreparedby) return setError("Prepared By is required");

    if (dischargeForm.discount && Number(dischargeForm.discount) > 0 && !dischargeForm.disAuthority) {
      return setError("Discount diya gaya hai, isliye Dis. Authority select karna zaroori hai.");
    }
    setDischargingPatient(true); setError(null);
    try {
      const payload = {
        IPDID: selectedPatient.value,
        disdate: dischargeForm.dischargeDate,
        invPref: "BILL",
        patientsSTS: dischargeForm.patientStatus ? "Dead" : "Alive",
        remarks: dischargeForm.remarks,
        patDieOrLive: dischargeForm.patientStatus,
        totalAmount: dischargeForm.totalAmount,
        discount: dischargeForm.discount,
        payable: dischargeForm.payable,
        received: dischargeForm.received,
        due: dischargeForm.due,
        invDates: dischargeForm.billDate,
        nofdays: String(dischargeForm.nofDays),
        docPreparedby: dischargeForm.docPreparedby,
        loginID: sessionStorage.getItem("loginId") || "System",
        hid: Number(sessionStorage.getItem("hospitalId")),
        syncSts: false,
        disAuthority: dischargeForm.disAuthority,
        requestedby: sessionStorage.getItem("loginId") || "System",
        createdBy: sessionStorage.getItem("loginId") || "System",
      };

     const res = await customFetch(`/DischargeDt`, {
  method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload),
});

      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || "Failed");
      }

      const savedRecord = await res.json();
      const dischargeDetails = savedRecord.dischargeDetails || savedRecord.discharge || savedRecord;
      const patientDetails = savedRecord.patientDetails || savedRecord.patient || (selectedPatient.raw ? selectedPatient.raw : null);

      const receiptObj = {
        billDate: dischargeDetails.invDates || dischargeForm.billDate,
        dischargeDate: dischargeDetails.disdate || dischargeForm.dischargeDate,
        createdBy: sessionStorage.getItem("loginId") || "System",
        patient: patientDetails,
        careOff: patientDetails.careOff || patientDetails.careOf || "-", 
    careOfMobile: patientDetails.alternateMobileNo || patientDetails.careOfMobile || "-",
     services: [
          ...(savedRecord.serviceDetails || []).map(s => ({ name: s.serviceName || s.name, rate: s.servRate, qty: s.qty, amount: s.amount, unit: s.unit }))
        ],
        advanceDetails: savedRecord.advanceDetails || [],
        roomDetails: savedRecord.roomDetails || [],
        totalAmount: dischargeForm.totalAmount,
        discount: dischargeForm.discount,
        payable: dischargeForm.payable,
        received: Number(dischargeForm.received || 0),
        due: dischargeForm.due,
        rawServerResponse: savedRecord,
      };

      setReceiptData(receiptObj);
      setShowReceipt(true);
      setMessage("Patient discharged successfully!");
      setDischargeForm(prev => ({ ...prev, discountValue: 0, received: 0, docPreparedby: "" }));
      fetchAdvancePayments(selectedPatient.value);
      fetchServices(selectedPatient.value);
      fetchRooms(selectedPatient.value);
      fetchFinancialSummary(selectedPatient.value);
    } catch (e) { setError(e.message); } finally { setDischargingPatient(false); }
  };

  // ==================== CHECK PENDING DUES ====================
  const hasPendingDues = finSummary && (Number(finSummary.billingDue) > 0 || Number(finSummary.medicineDue) > 0);


  // ==================== REACT-SELECT STYLES ====================
  const selectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: '26px',
      height: '26px',
      fontSize: '11px',
      borderRadius: '4px',
      borderColor: state.isFocused ? 'var(--theme-color, #0088cc)' : '#cbd5e0',
      boxShadow: state.isFocused ? '0 0 0 2px color-mix(in srgb, var(--theme-color, #0088cc), transparent 85%)' : 'none',
      '&:hover': { borderColor: 'var(--theme-color, #0088cc)' },
    }),
    valueContainer: (base) => ({ ...base, padding: '0 6px', height: '24px' }),
    indicatorsContainer: (base) => ({ ...base, height: '24px' }),
    dropdownIndicator: (base) => ({ ...base, padding: '0 4px' }),
    clearIndicator: (base) => ({ ...base, padding: '0 4px' }),
    input: (base) => ({ ...base, margin: '0', padding: '0', fontSize: '11px' }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    menu: (base) => ({ ...base, zIndex: 9999 }),
    option: (base, state) => ({
      ...base,
      fontSize: '11px',
      padding: '6px 10px',
      backgroundColor: state.isSelected
        ? 'var(--theme-color, #0088cc)'
        : state.isFocused
          ? 'color-mix(in srgb, var(--theme-color, #0088cc), white 90%)'
          : 'white',
      color: state.isSelected ? 'white' : '#333',
      cursor: 'pointer',
      '&:active': { backgroundColor: 'var(--theme-color, #0088cc)', color: 'white' }
    }),
    placeholder: (base) => ({ ...base, color: '#a0aec0', fontSize: '11px' }),
    singleValue: (base) => ({ ...base, fontSize: '11px' }),
  };

  const headerSelectStyles = {
    ...selectStyles,
    control: (base, state) => ({
      ...base,
      minHeight: '28px',
      height: '28px',
      fontSize: '11px',
      borderRadius: '4px',
      borderColor: state.isFocused ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.4)',
      backgroundColor: 'rgba(255,255,255,0.15)',
      boxShadow: state.isFocused ? '0 0 0 2px rgba(255,255,255,0.3)' : 'none',
      '&:hover': { borderColor: 'rgba(255,255,255,0.7)' },
    }),
    valueContainer: (base) => ({ ...base, padding: '0 6px', height: '26px' }),
    indicatorsContainer: (base) => ({ ...base, height: '26px' }),
    dropdownIndicator: (base) => ({ ...base, padding: '0 4px', color: 'rgba(255,255,255,0.7)' }),
    clearIndicator: (base) => ({ ...base, padding: '0 4px', color: 'rgba(255,255,255,0.7)' }),
    input: (base) => ({ ...base, margin: '0', padding: '0', fontSize: '11px', color: 'white' }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    menu: (base) => ({ ...base, zIndex: 9999 }),
    option: (base, state) => ({
      ...base,
      fontSize: '11px',
      padding: '6px 10px',
      backgroundColor: state.isSelected
        ? 'var(--theme-color, #0088cc)'
        : state.isFocused
          ? 'color-mix(in srgb, var(--theme-color, #0088cc), white 90%)'
          : 'white',
      color: state.isSelected ? 'white' : '#333',
      cursor: 'pointer',
    }),
    placeholder: (base) => ({ ...base, color: 'rgba(255,255,255,0.65)', fontSize: '11px' }),
    singleValue: (base) => ({ ...base, fontSize: '11px', color: 'white' }),
  };

  // ==================== RENDER ====================
  return (
    <div className="dc-wrap">
      <div className="dc-card">

        {/* ===== HEADER ===== */}
        <div className="dc-header">
          <div className="dc-header-inner">
            <div className="dc-header-brand">
              <div className="dc-header-icon"><i className="bi bi-file-medical"></i></div>
              <span className="dc-header-title">Discharge Bill</span>
            </div>

            <div className="dc-header-search react-select-container">
              <AsyncSelect
                className="react-select-container"
                cacheOptions
                defaultOptions
                loadOptions={loadIpdOptions}
                onChange={setSelectedPatient}
                value={selectedPatient}
                isClearable
                placeholder="🔍  Type patient name, mobile or IPD No..."
                styles={headerSelectStyles}
                menuPortalTarget={document.body}
              />
            </div>

            {selectedPatient && (
              <button
                type="button"
                className="dc-toggle-btn"
                onClick={() => setShowDischargeForm(!showDischargeForm)}
              >
                {showDischargeForm
                  ? <><i className="bi bi-eye-slash"></i> Hide</>
                  : <><i className="bi bi-eye"></i> Show</>
                }
              </button>
            )}
          </div>
        </div>

        <div className="dc-body">

          {/* Alerts */}
          {error && (
            <div className="dc-alert dc-alert-danger">
              <i className="bi bi-exclamation-triangle-fill"></i>
              <span>{error}</span>
              <button type="button" className="dc-alert-close" onClick={() => setError("")}><i className="bi bi-x"></i></button>
            </div>
          )}
          {message && (
            <div className="dc-alert dc-alert-success">
              <i className="bi bi-check-circle-fill"></i>
              <span>{message}</span>
              <button type="button" className="dc-alert-close" onClick={() => setMessage("")}><i className="bi bi-x"></i></button>
            </div>
          )}
          
          {hasPendingDues && (
             <div className="dc-alert dc-alert-danger dc-fw-bold" style={{ fontSize: '12px', border: '1px solid #dc2626' }}>
               <i className="bi bi-shield-lock-fill" style={{ fontSize: '14px' }}></i>
               <span>Discharge Restricted: Clear pending Billing or Medicine dues first!</span>
             </div>
          )}

          {selectedPatient && (
            <>
              {showDischargeForm && (
                <div className="dc-layout-split">
                  
                  {/* LEFT 70% - FORM */}
                  <div className="dc-layout-left">
                    <form ref={formRef} onSubmit={(e) => { e.preventDefault(); handleDischargeSubmit(); }}>
                      <div className="dc-section">
                        <div className="dc-section-title">
                          <i className="bi bi-receipt-cutoff"></i>
                          <span>Discharge Details</span>
                        </div>
                        <div className="dc-section-body">

                          <div className="dc-grid dc-grid-12 dc-mb1">
                            <div style={{ gridColumn: 'span 2' }}>
                              <label className="dc-label">Bill Date</label>
                              <input type="date" className="dc-input" value={dischargeForm.billDate} onChange={(e) => handleDischargeFormChange("billDate", e.target.value)} />
                            </div>
                            <div style={{ gridColumn: 'span 2' }}>
                              <label className="dc-label">Discharge Date</label>
                              <input type="date" className="dc-input" value={dischargeForm.dischargeDate} onChange={(e) => handleDischargeFormChange("dischargeDate", e.target.value)} />
                            </div>
                            <div style={{ gridColumn: 'span 1' }}>
                              <label className="dc-label">Days</label>
                              <input type="number" className="dc-input" value={dischargeForm.nofDays} readOnly disabled />
                            </div>
                            <div style={{ gridColumn: 'span 2' }}>
                              <label className="dc-label dc-clr-success">Grand Total (₹)</label>
                              <input type="number" className="dc-input dc-fw-bold dc-clr-success" value={dischargeForm.totalAmount.toFixed(2)} readOnly disabled />
                              <small className="dc-hint">
                                Svc: ₹{(dischargeForm.serviceTotal || 0).toFixed(2)} | Rm: ₹{(dischargeForm.roomTotal || 0).toFixed(2)}
                              </small>
                            </div>
                            <div style={{ gridColumn: 'span 2' }} className="react-select-container">
                              <label className="dc-label">Discount Type</label>
                              <Select
                                options={discountTypeOptions}
                                value={discountTypeOptions.find(opt => opt.value === dischargeForm.discountType)}
                                onChange={handleDiscountTypeChange}
                                styles={selectStyles}
                                isSearchable={false}
                                placeholder="Select Type"
                                menuPortalTarget={document.body}
                              />
                            </div>
                            <div style={{ gridColumn: 'span 2' }}>
                              <label className="dc-label">Discount Value</label>
                              <input type="number" className="dc-input" value={dischargeForm.discountValue} onFocus={(e) => e.target.select()} onChange={(e) => handleDischargeFormChange("discountValue", e.target.value)} min="0" step="0.01" />
                            </div>
                            <div style={{ gridColumn: 'span 1' }}>
                              <label className="dc-label">Calc. Disc.</label>
                              <input type="number" className="dc-input" value={dischargeForm.discount.toFixed(2)} readOnly disabled />
                            </div>
                          </div>

                          <div className="dc-grid dc-grid-10 dc-mb1">
                            <div style={{ gridColumn: 'span 2' }} className="react-select-container">
                              <label className="dc-label">Dis. Authority</label>
                              <DiscountAuthorityDropdown
                                value={dischargeForm.disAuthority}
                                onChange={(selectedOption) => handleDischargeFormChange("disAuthority", selectedOption ? selectedOption.value : "")}
                                styles={selectStyles} 
                                className="dc-input" 
                              />
                            </div>
                            <div style={{ gridColumn: 'span 2' }}>
                              <label className="dc-label">Total Advance</label>
                              <input type="number" className="dc-input" value={dischargeForm.advance.toFixed(2)} readOnly disabled />
                            </div>
                            <div style={{ gridColumn: 'span 2' }}>
                              <label className="dc-label">Payable Amt</label>
                              <input type="number" className="dc-input dc-fw-bold" value={dischargeForm.payable.toFixed(2)} readOnly disabled />
                            </div>
                            <div style={{ gridColumn: 'span 2' }}>
                              <label className="dc-label">Received Now</label>
                              <input type="number" className="dc-input" value={dischargeForm.received} onFocus={(e) => e.target.select()} onChange={(e) => handleDischargeFormChange("received", e.target.value)} min="0" step="0.01" />
                            </div>
                            <div style={{ gridColumn: 'span 2' }}>
                              <label className="dc-label dc-clr-danger">Due Amount</label>
                              <input type="number" className="dc-input dc-fw-bold dc-clr-danger" value={dischargeForm.due.toFixed(2)} readOnly disabled />
                            </div>
                          </div>

                          <div className="dc-grid dc-grid-12 dc-mb1 dc-align-end">
                            <div style={{ gridColumn: 'span 3' }}>
                              <label className="dc-label">Prepared By <span className="dc-required">*</span></label>
                              <input type="text" className="dc-input" value={dischargeForm.docPreparedby} onChange={(e) => handleDischargeFormChange("docPreparedby", e.target.value)} placeholder="Enter Name" />
                            </div>
                            <div style={{ gridColumn: 'span 7' }}>
                              <label className="dc-label">Remarks</label>
                              <textarea className="dc-textarea" rows="1" value={dischargeForm.remarks} onChange={(e) => handleDischargeFormChange("remarks", e.target.value)} placeholder="Enter remarks..." />
                            </div>
                            <div style={{ gridColumn: 'span 2' }} className="dc-status-wrap">
                              <label className="dc-status-label">
                                <input type="checkbox" className="dc-checkbox" checked={dischargeForm.patientStatus} onChange={(e) => handleDischargeFormChange("patientStatus", e.target.checked)} />
                                <span className={dischargeForm.patientStatus ? "dc-clr-danger dc-fw-bold" : "dc-clr-success dc-fw-bold"}>
                                  {dischargeForm.patientStatus ? "Expired" : "Alive"}
                                </span>
                              </label>
                            </div>
                          </div>

                        </div>
                      </div>

                      <div className="dc-form-footer">
                        <button type="submit" className="dc-btn-submit" disabled={dischargingPatient || hasPendingDues}>
                          {dischargingPatient
                            ? <><span className="dc-spinner"></span>Processing...</>
                            : <><i className="bi bi-check-circle"></i> Finalize Discharge</>
                          }
                        </button>
                      </div>
                    </form>
                  </div>

                  {/* RIGHT 30% - FINANCIAL OVERVIEW */}
                  <div className="dc-layout-right">
                    <div className="dc-section dc-sticky-panel">
                      <div className="dc-section-title" style={{ background: '#0f172a' }}>
                         <i className="bi bi-pie-chart-fill"></i>
                         <span>Financial Overview</span>
                      </div>
                      <div className="dc-section-body" style={{ padding: '12px', background: '#f8fafc' }}>
                        
                        {/* Package Info */}
                        {finSummary && finSummary.hasPackage ? (
                          <div className="fin-box pkg-box">
                            <div className="fin-box-title"><i className="bi bi-box-seam"></i> Package Applied</div>
                            <div className="fin-box-name">{finSummary.packageName}</div>
                            <div className="fin-box-amt">₹{finSummary.packageAmount.toFixed(2)}</div>
                            <div className="fin-badges">
                              {finSummary.isBillingIncluded && <span className="dc-badge dc-badge-blue">Gen. Billing Incl.</span>}
                              {finSummary.isMedicineIncluded && <span className="dc-badge dc-badge-green">Medicine Incl.</span>}
                            </div>
                          </div>
                        ) : (
                          <div className="fin-box nopkg-box">
                            <i className="bi bi-info-circle"></i> No Package Applied
                          </div>
                        )}

                        {/* Advance */}
                        <div className="fin-row">
                          <span className="fin-label">Total Advance:</span>
                          <span className="fin-val dc-fw-bold">₹{dischargeForm.advance.toFixed(2)}</span>
                        </div>

                        {/* General Billing */}
                        <div className="fin-card mt-2">
                           <div className="fin-card-header"><i className="bi bi-receipt"></i> General Billing</div>
                           <div className="fin-card-body">
                              <div className="fin-mini-row"><span>Payable:</span> <span>₹{finSummary?.billingPayable?.toFixed(2) || "0.00"}</span></div>
                              <div className="fin-mini-row"><span>Paid:</span> <span className="dc-clr-success">₹{finSummary?.billingPaid?.toFixed(2) || "0.00"}</span></div>
                              <div className="fin-mini-row dc-fw-bold">
                                <span>Due:</span> 
                                <span className={finSummary?.billingDue > 0 ? 'dc-clr-danger' : ''}>₹{finSummary?.billingDue?.toFixed(2) || "0.00"}</span>
                              </div>
                           </div>
                        </div>

                        {/* Medicine Sales */}
                        <div className="fin-card mt-2">
                           <div className="fin-card-header"><i className="bi bi-capsule"></i> Medicine Sales</div>
                           <div className="fin-card-body">
                              <div className="fin-mini-row"><span>Payable:</span> <span>₹{finSummary?.medicinePayable?.toFixed(2) || "0.00"}</span></div>
                              <div className="fin-mini-row"><span>Paid:</span> <span className="dc-clr-success">₹{finSummary?.medicinePaid?.toFixed(2) || "0.00"}</span></div>
                              <div className="fin-mini-row dc-fw-bold">
                                <span>Due:</span> 
                                <span className={finSummary?.medicineDue > 0 ? 'dc-clr-danger' : ''}>₹{finSummary?.medicineDue?.toFixed(2) || "0.00"}</span>
                              </div>
                           </div>
                        </div>

                      </div>
                    </div>
                  </div>

                </div>
              )}

              {/* ===== Advance Payments Table ===== */}
              <div className="dc-section dc-mt2">
                <div className="dc-section-title"><i className="bi bi-cash-coin"></i><span>Advance Payments</span></div>
                <div className="dc-section-body dc-p0">
                  <div className="dc-table-wrap">
                    <table className="dc-table">
                      <thead>
                        <tr><th>Amount</th><th>Date</th><th>Mode</th><th>Chq No</th><th>Chq Date</th><th>Remarks</th><th>Action</th></tr>
                      </thead>
                      <tbody>
                        {advancePayments.length === 0
                          ? <tr><td colSpan="7" className="dc-empty">No records</td></tr>
                          : advancePayments.map(r => (
                            <tr key={r.advID}>
                              <td>{advEditId === r.advID ? <input type="number" className="dc-table-input" value={advEditData.adv_Amt} onFocus={(e) => e.target.select()} onChange={(e) => handleAdvChange("adv_Amt", e.target.value)} /> : r.adv_Amt?.toFixed(2)}</td>
                              <td>{advEditId === r.advID ? <input type="date" className="dc-table-input" value={advEditData.recvDate} onChange={(e) => handleAdvChange("recvDate", e.target.value)} /> : r.recvDate?.substring(0, 10)}</td>
                              <td>{advEditId === r.advID ? <select className="dc-table-input" value={advEditData.payMode} onChange={(e) => handleAdvChange("payMode", e.target.value)}><option value="Cash">Cash</option><option value="UPI">UPI</option><option value="Card">Card</option><option value="Cheque">Cheque</option></select> : r.payMode}</td>
                              <td>{advEditId === r.advID ? <input type="text" className="dc-table-input" value={advEditData.chqNo} onChange={(e) => handleAdvChange("chqNo", e.target.value)} disabled={advEditData.payMode !== "Cheque"} /> : r.chqNo || "-"}</td>
                              <td>{advEditId === r.advID ? <input type="date" className="dc-table-input" value={advEditData.chqDate} onChange={(e) => handleAdvChange("chqDate", e.target.value)} disabled={advEditData.payMode !== "Cheque"} /> : r.chqDate?.substring(0, 10) || "-"}</td>
                              <td>{advEditId === r.advID ? <input type="text" className="dc-table-input" value={advEditData.remarks} onChange={(e) => handleAdvChange("remarks", e.target.value)} /> : r.remarks || "-"}</td>
                              <td>
                                {advEditId === r.advID
                                  ? <div className="dc-btn-group"><button type="button" className="dc-icon-btn dc-save" onClick={saveAdvance}><i className="bi bi-check-lg"></i></button><button type="button" className="dc-icon-btn dc-cancel" onClick={() => setAdvEditId(null)}><i className="bi bi-x-lg"></i></button></div>
                                  : <div className="dc-btn-group"><button type="button" className="dc-icon-btn dc-edit" onClick={() => startAdvEditing(r)}><i className="bi bi-pencil-square"></i></button><button type="button" className="dc-icon-btn dc-delete" onClick={() => deleteAdvance(r.advID)}><i className="bi bi-trash"></i></button></div>
                                }
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

               {/* ===== Consolidated Service Details ===== */}
              <div className="dc-section dc-mt2">
                <div className="dc-section-title"><i className="bi bi-list-stars"></i><span>Consolidated Services (Summary)</span></div>
                <div className="dc-section-body dc-p0">
                  <div className="dc-table-wrap">
                    <table className="dc-table">
                      <thead>
                        <tr>
                          <th>Service Name</th>
                          <th style={{ width: '100px' }} className="dc-text-center">Total Qty</th>
                          <th style={{ width: '100px' }} className="dc-text-right">Rate</th>
                          <th style={{ width: '120px' }} className="dc-text-right">Total Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {consolidatedServices.length === 0
                          ? <tr><td colSpan="4" className="dc-empty">No services found.</td></tr>
                          : consolidatedServices.map((s, idx) => (
                            <tr key={idx}>
                              <td>{s.serviceName}</td>
                              <td className="dc-text-center">{s.qty}</td>
                              <td className="dc-text-right">{Number(s.servRate).toFixed(2)}</td>
                              <td className="dc-text-right dc-fw-bold dc-clr-success">₹{Number(s.amount).toFixed(2)}</td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* ===== Service Details Table ===== */}
              <div className="dc-section dc-mt2">
                <div className="dc-section-title"><i className="bi bi-tools"></i><span>Service Details (Individual Entries)</span></div>
                <div className="dc-section-body dc-p0">
                  <div className="dc-table-wrap">
                    <table className="dc-table">
                      <thead>
                        <tr>
                          <th style={{ width: '30px' }}><input type="checkbox" checked={selectAllServices} onChange={toggleSelectAllServices} /></th>
                          <th>Service Name</th>
                          <th>Date</th>
                          <th style={{ width: '70px' }}>Qty</th>
                          <th style={{ width: '90px' }}>Rate</th>
                          <th style={{ width: '90px' }}>Amount</th>
                          <th>Remarks</th>
                          <th style={{ width: '80px' }}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {services.length === 0
                          ? <tr><td colSpan="8" className="dc-empty">No services found.</td></tr>
                          : services.map(s => (
                            <tr key={s.servSrNo}>
                              <td><input type="checkbox" checked={selectedServiceIds.includes(s.servSrNo)} onChange={() => toggleServiceSelect(s.servSrNo)} /></td>
                              <td>{s.serviceName}</td>
                              
                              {serviceEditId === s.servSrNo ? (
                                <>
                                  <td><input type="datetime-local" className="dc-table-input" value={serviceEditData.servdate || ""} onChange={(e) => handleServiceChange("servdate", e.target.value)} /></td>
                                  <td><input type="number" className="dc-table-input" value={serviceEditData.qty} onFocus={(e) => e.target.select()} onChange={(e) => handleServiceChange("qty", e.target.value)} min="1" step="0.1" /></td>
                                  <td><input type="number" className="dc-table-input" value={serviceEditData.servRate} onFocus={(e) => e.target.select()} onChange={(e) => handleServiceChange("servRate", e.target.value)} min="0" step="0.01" /></td>
                                  <td>{serviceEditData.amount}</td>
                                  <td><input type="text" className="dc-table-input" value={serviceEditData.remarks} onChange={(e) => handleServiceChange("remarks", e.target.value)} /></td>
                                  <td>
                                    <div className="dc-btn-group">
                                      <button type="button" className="dc-icon-btn dc-save" onClick={saveService}><i className="bi bi-check-lg"></i></button>
                                      <button type="button" className="dc-icon-btn dc-cancel" onClick={() => setServiceEditId(null)}><i className="bi bi-x-lg"></i></button>
                                    </div>
                                  </td>
                                </>
                              ) : (
                                <>
                                  <td className="small text-muted">
                                    {s.servdate ? new Date(s.servdate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
                                  </td>
                                  <td>{s.qty}</td>
                                  <td>{s.servRate}</td>
                                  <td>{s.amount}</td>
                                  <td>{s.remarks || "-"}</td>
                                  <td>
                                    <div className="dc-btn-group">
                                      <button type="button" className="dc-icon-btn dc-edit" onClick={() => startServiceEditing(s)}><i className="bi bi-pencil-square"></i></button>
                                      <button type="button" className="dc-icon-btn dc-delete" onClick={() => deleteService(s.servSrNo)}><i className="bi bi-trash"></i></button>
                                    </div>
                                  </td>
                                </>
                              )}
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                  {selectedServiceIds.length > 0 && (
                    <div className="dc-bulk-bar">
                      <button type="button" className="dc-btn-del-bulk" onClick={() => deleteService()}>
                        <i className="bi bi-trash"></i> Delete Selected ({selectedServiceIds.length})
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* ===== Room Bookings Table ===== */}
              <div className="dc-section dc-mt2 dc-mb2">
                <div className="dc-section-title"><i className="bi bi-hospital"></i><span>Room Bookings</span></div>
                <div className="dc-section-body dc-p0">
                  <div className="dc-table-wrap">
                    <table className="dc-table">
                      <thead>
                        <tr><th>Category</th><th>Room No</th><th>Entry Date</th><th>Exit Date</th><th>Rate</th><th>Remarks</th><th>Action</th></tr>
                      </thead>
                      <tbody>
                        {rooms.length === 0
                          ? <tr><td colSpan="7" className="dc-empty">No rooms found.</td></tr>
                          : rooms.map(r => (
                            <tr key={r.rM_TID}>
                              <td>{r.roomCatge}</td>
                              <td>{r.roomNo}</td>
                              <td>{r.entryDate ? new Date(r.entryDate).toLocaleString() : "-"}</td>
                              <td><input type="datetime-local" className="dc-table-input" value={roomExitMap[r.rM_TID] || ""} onChange={(e) => handleRoomExitChange(r.rM_TID, e.target.value)} /></td>
                              <td>{r.roomRate}</td>
                              <td>{r.remarks || "-"}</td>
                              <td>
                                <div className="dc-btn-group">
                                  <button type="button" className="dc-icon-btn dc-edit" title="Update Exit" onClick={() => updateExitDate(r)} disabled={!roomExitMap[r.rM_TID]}><i className="bi bi-box-arrow-right"></i></button>
                                  <button type="button" className="dc-icon-btn dc-delete" onClick={() => handleRoomDelete(r.rM_TID)}><i className="bi bi-trash"></i></button>
                                </div>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {companyDetails && showReceipt && receiptData && (
        <DischargeReceiptModal
          show={showReceipt}
          data={receiptData}
          patient={receiptData.patient}
          advanceDetails={receiptData.advanceDetails}
          dischargeDetails={receiptData.rawServerResponse?.discharge || receiptData.rawServerResponse}
          serviceDetails={receiptData.services}
          roomDetails={receiptData.roomDetails}
          companyDetails={companyDetails}
          onClose={() => setShowReceipt(false)}
          onPrint={() => window.print()}
        />
      )}

      {/* ==================== PURE CSS — ZERO BOOTSTRAP ==================== */}
      <style jsx>{`

        *, *::before, *::after { box-sizing: border-box; }

        /* ── Wrap & Card ── */
        .dc-wrap  { padding: 0; font-family: system-ui, sans-serif; }
        .dc-card  {
          background: #fff;
          border-radius: 8px;
          box-shadow: 0 1px 8px rgba(0,0,0,0.06);
          border: 1px solid #e0e6ed;
          overflow: visible;
        }

        /* ── Header ── */
        .dc-header {
          background: var(--theme-color, #0088cc);
          padding: 5px 12px;
          border-bottom: 2px solid rgba(0,0,0,0.1);
          border-radius: 8px 8px 0 0;
        }
        .dc-header-inner {
          display: flex;
          align-items: center;
          gap: 10px;
          width: 100%;
        }
        .dc-header-brand {
          display: flex;
          align-items: center;
          gap: 7px;
          flex-shrink: 0;
        }
        .dc-header-icon {
          width: 28px; height: 28px;
          background: rgba(255,255,255,0.2);
          border-radius: 5px;
          display: flex; align-items: center; justify-content: center;
          font-size: 14px; color: #fff;
          flex-shrink: 0;
        }
        .dc-header-title {
          color: #fff;
          font-size: 13px;
          font-weight: 700;
          white-space: nowrap;
        }
        .dc-header-search {
          flex: 1 1 0;
          min-width: 0;
        }
        .dc-toggle-btn {
          flex-shrink: 0;
          padding: 3px 10px;
          border-radius: 4px;
          border: 1px solid rgba(255,255,255,0.5);
          background: rgba(255,255,255,0.15);
          color: #fff;
          font-size: 11px;
          font-weight: 600;
          cursor: pointer;
          transition: background 0.15s;
          display: inline-flex;
          align-items: center;
          gap: 4px;
          white-space: nowrap;
        }
        .dc-toggle-btn:hover { background: rgba(255,255,255,0.3); }

        /* ── Body ── */
        .dc-body { padding: 10px 12px; }

        /* ── Layout Split ── */
        .dc-layout-split { display: flex; gap: 15px; align-items: flex-start; }
        .dc-layout-left { flex: 0 0 70%; width: 70%; }
        .dc-layout-right { flex: 0 0 calc(30% - 15px); width: calc(30% - 15px); position: sticky; top: 10px; }

        /* ── Section ── */
        .dc-section {
          background: #f8f9fa;
          border: 1px solid #e0e6ed;
          border-radius: 6px;
          margin-bottom: 8px;
          overflow: visible;
        }
        .dc-sticky-panel {
          box-shadow: 0 4px 12px rgba(0,0,0,0.05);
          border-color: #cbd5e0;
        }
        .dc-mt2 { margin-top: 8px; }
        .dc-mb2 { margin-bottom: 8px; }

        .dc-section-title {
          background: var(--theme-color, #0088cc);
          padding: 4px 10px;
          display: flex;
          align-items: center;
          gap: 6px;
          border-radius: 5px 5px 0 0;
        }
        .dc-section-title i,
        .dc-section-title span { font-size: 11px; color: #fff; font-weight: 600; }

        .dc-section-body {
          padding: 8px;
          background: #fff;
          border-radius: 0 0 6px 6px;
        }
        .dc-p0 { padding: 0 !important; }

        /* ── Financial Panel Styling ── */
        .fin-box { padding: 10px; border-radius: 5px; margin-bottom: 12px; border: 1px solid transparent; }
        .pkg-box { background: #eff6ff; border-color: #bfdbfe; }
        .nopkg-box { background: #f8fafc; border-color: #e2e8f0; color: #64748b; font-size: 11px; text-align: center; }
        .fin-box-title { font-size: 10px; color: #3b82f6; font-weight: 700; text-transform: uppercase; margin-bottom: 4px; }
        .fin-box-name { font-size: 13px; font-weight: 700; color: #1e3a8a; line-height: 1.2; }
        .fin-box-amt { font-size: 16px; font-weight: 800; color: #2563eb; margin: 4px 0; }
        .fin-badges { display: flex; gap: 4px; flex-wrap: wrap; margin-top: 6px; }
        .dc-badge { padding: 2px 6px; border-radius: 12px; font-size: 9px; font-weight: 600; }
        .dc-badge-blue { background: #dbeafe; color: #1e40af; border: 1px solid #bfdbfe; }
        .dc-badge-green { background: #dcfce7; color: #166534; border: 1px solid #bbf7d0; }

        .fin-row { display: flex; justify-content: space-between; align-items: center; padding: 6px 0; border-bottom: 1px dashed #e2e8f0; font-size: 11px; }
        .fin-label { color: #475569; font-weight: 600; }
        .fin-val { color: #0f172a; font-size: 12px; }

        .fin-card { border: 1px solid #e2e8f0; border-radius: 5px; overflow: hidden; }
        .fin-card-header { background: #f1f5f9; padding: 4px 8px; font-size: 10px; font-weight: 700; color: #334155; border-bottom: 1px solid #e2e8f0; }
        .fin-card-body { padding: 6px 8px; background: #fff; }
        .fin-mini-row { display: flex; justify-content: space-between; font-size: 10px; padding: 2px 0; color: #475569; }

        /* ── CSS Grid layout ── */
        .dc-grid {
          display: grid;
          gap: 6px;
          align-items: start;
        }
        .dc-grid-12 { grid-template-columns: repeat(12, 1fr); }
        .dc-grid-10 { grid-template-columns: repeat(10, 1fr); }
        .dc-mb1     { margin-bottom: 6px; }
        .dc-align-end { align-items: end; }

        /* ── Labels ── */
        .dc-label {
          display: block;
          font-size: 10px;
          font-weight: 600;
          color: #2c3e50;
          margin-bottom: 1px;
          line-height: 1.2;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .dc-clr-success { color: #16a34a !important; }
        .dc-clr-danger  { color: #dc2626 !important; }
        .dc-fw-bold     { font-weight: 700 !important; }
        .dc-required    { color: #e74c3c; }

        .dc-hint {
          font-size: 9px;
          color: #888;
          display: block;
          line-height: 1.2;
          margin-top: 1px;
        }

        /* ── Inputs — height 26 px ── */
        .dc-input,
        .dc-textarea,
        .dc-table-input {
          width: 100%;
          height: 26px;
          min-height: 26px;
          padding: 2px 6px;
          border: 1px solid #cbd5e0;
          border-radius: 4px;
          font-size: 11px;
          color: #2c3e50;
          background: #fff;
          outline: none;
          transition: border-color 0.15s, box-shadow 0.15s;
          line-height: 1;
        }
        .dc-textarea {
          resize: none;
          overflow-y: auto;
          padding-top: 4px;
        }
        .dc-input:focus,
        .dc-textarea:focus,
        .dc-table-input:focus {
          border-color: var(--theme-color, #0088cc);
          box-shadow: 0 0 0 2px color-mix(in srgb, var(--theme-color, #0088cc), transparent 85%);
        }
        .dc-input:disabled,
        .dc-textarea:disabled {
          background: #f3f4f6;
          color: #555;
          cursor: not-allowed;
        }
        .dc-table-input:disabled {
          background: #f3f4f6;
          color: #999;
          cursor: not-allowed;
        }

        /* Patient status row */
        .dc-status-wrap {
          display: flex;
          align-items: center;
          padding-bottom: 2px;
        }
        .dc-status-label {
          display: flex;
          align-items: center;
          gap: 5px;
          font-size: 11px;
          cursor: pointer;
        }
        .dc-checkbox { margin: 0; cursor: pointer; }

        /* ── Form footer ── */
        .dc-form-footer {
          margin-top: 6px;
          padding-top: 8px;
          border-top: 1px solid #e0e6ed;
          text-align: right;
        }

        /* ── Submit button ── */
        .dc-btn-submit {
          padding: 5px 18px;
          border-radius: 5px;
          border: none;
          background: var(--theme-color, #0088cc);
          color: #fff;
          font-size: 11px;
          font-weight: 600;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 5px;
          transition: opacity 0.15s, transform 0.15s, box-shadow 0.15s;
        }
        .dc-btn-submit:hover:not(:disabled) {
          opacity: 0.9;
          transform: translateY(-1px);
          box-shadow: 0 2px 8px rgba(0,0,0,0.15);
        }
        .dc-btn-submit:disabled { opacity: 0.6; cursor: not-allowed; filter: grayscale(1); }

        .dc-spinner {
          display: inline-block;
          width: 12px; height: 12px;
          border: 2px solid rgba(255,255,255,0.4);
          border-top-color: #fff;
          border-radius: 50%;
          animation: dc-spin 0.7s linear infinite;
          margin-right: 4px;
        }
        @keyframes dc-spin { to { transform: rotate(360deg); } }

        /* ── Tables ── */
        .dc-table-wrap { overflow-x: auto; }
        .dc-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 11px;
        }
        .dc-table thead tr {
          background: var(--theme-color, #0088cc) !important;
        }
        .dc-table th {
          color: #fff;
          font-weight: 600;
          padding: 5px 8px;
          text-transform: uppercase;
          font-size: 10px;
          border: none;
          text-align: left;
          background: transparent;
          white-space: nowrap;
        }
        .dc-table td {
          padding: 3px 8px;
          border-bottom: 1px solid #e0e6ed;
          vertical-align: middle;
        }
        .dc-table tbody tr:hover { background: #f0f7ff; }
        
        .dc-text-right { text-align: right !important; }
        .dc-text-center { text-align: center !important; }

        .dc-empty {
          text-align: center;
          padding: 10px;
          color: #999;
          font-size: 11px;
        }

        .dc-bulk-bar { padding: 4px 8px; border-top: 1px solid #e0e6ed; }
        .dc-btn-del-bulk {
          background: #fee2e2;
          color: #dc2626;
          border: none;
          padding: 3px 8px;
          border-radius: 4px;
          font-size: 11px;
          font-weight: 600;
          cursor: pointer;
        }

        /* ── Icon action buttons ── */
        .dc-btn-group { display: flex; gap: 3px; justify-content: center; }
        .dc-icon-btn {
          width: 22px; height: 22px;
          border-radius: 3px;
          border: none;
          display: flex; align-items: center; justify-content: center;
          cursor: pointer;
          font-size: 11px;
          transition: background 0.15s, color 0.15s;
        }
        .dc-edit  { background: color-mix(in srgb, var(--theme-color, #0088cc), white 88%); color: var(--theme-color, #0088cc); }
        .dc-edit:hover  { background: var(--theme-color, #0088cc); color: #fff; }
        .dc-delete { background: #fee2e2; color: #dc2626; }
        .dc-delete:hover { background: #dc2626; color: #fff; }
        .dc-save   { background: #d1fae5; color: #059669; }
        .dc-save:hover   { background: #059669; color: #fff; }
        .dc-cancel { background: #f3f4f6; color: #6b7280; }
        .dc-cancel:hover { background: #e5e7eb; }
        .dc-icon-btn:disabled { opacity: 0.4; cursor: not-allowed; }

        /* ── Alerts ── */
        .dc-alert {
          display: flex;
          align-items: center;
          gap: 7px;
          padding: 6px 10px;
          border-radius: 5px;
          font-size: 11px;
          margin-bottom: 8px;
          position: relative;
          animation: dc-slidein 0.25s ease;
        }
        .dc-alert-danger  { background: #fff5f5; color: #c53030; border: 1px solid #fc8181; }
        .dc-alert-success { background: #f0fdf4; color: #22543d; border: 1px solid #68d391; }
        .dc-alert-close {
          position: absolute; right: 8px;
          background: transparent; border: none;
          cursor: pointer; font-size: 14px;
          color: inherit; line-height: 1;
        }
        @keyframes dc-slidein {
          from { opacity: 0; transform: translateY(-4px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        /* ── Responsive ── */
        @media (max-width: 900px) {
          .dc-layout-split { flex-direction: column; }
          .dc-layout-left, .dc-layout-right { flex: 0 0 100%; width: 100%; position: static; }
          .dc-grid-12 { grid-template-columns: repeat(6, 1fr); }
          .dc-grid-10 { grid-template-columns: repeat(4, 1fr); }
        }
        @media (max-width: 600px) {
          .dc-header-inner { flex-wrap: wrap; gap: 6px; }
          .dc-grid-12,
          .dc-grid-10 { grid-template-columns: repeat(2, 1fr); }
          .dc-grid-12 > div,
          .dc-grid-10 > div { grid-column: span 1 !important; }
          .dc-table-wrap { overflow-x: auto; }
          .dc-table     { min-width: 560px; }
        }
      `}</style>
    </div>
  );
};

export default DischargeBill;